import { useMutation, useQuery } from '@apollo/client/react';
import { useRouter } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import {
  RefreshControl,
  ScrollView,
  SectionList,
  type SectionListData,
  StyleSheet,
  View,
} from 'react-native';

import { ConfirmDialog } from '@/components/atoms/dialog';
import { ErrorState, LoadingState } from '@/components/atoms/state';
import { USER_PICKER_GET_USERS_VARIABLES } from '@/codegen/usermobile-bff/users/userPickerGetUsersVariables';
import {
  AiBatonEmptyState,
  ContactFilterSheet,
  DateRangeFilterSheet,
} from '@/components/molecules/ai-baton';
import { FilterDropdownChip } from '@/components/molecules/filter';
import { PaginationFooter } from '@/components/molecules/list/PaginationFooter';
import { SectionDateHeader } from '@/components/molecules/section-header';
import { UserPickerSheet, type UserOption } from '@/components/molecules/person-in-charge';
import {
  ClaimHandoffDocument,
  GetContactsDocument,
  GetHandoffsDocument,
  GetHandoffUnreadStateDocument,
  GetUrgentHandoffsDocument,
  GetUsersDocument,
  MeDocument,
  ReleaseHandoffDocument,
  type GetHandoffsQuery,
} from '@/generated/usermobile-bff/graphql';
import { useDebounce, usePaginatedFetchMore, useRefreshHandler } from '@/hooks';
import { useTranslation } from '@/i18n';
import {
  getHandoffSections,
  isHandoffAssignedToCurrentUser,
  type HandoffAction,
  type HandoffItem,
  type HandoffSection,
} from '@/models/handoffs';
import {
  HANDOFF_DATE_RANGE_TRANSLATION_KEYS,
  getHandoffDateRangeStart,
  getUnreadHandoffDateRanges,
  type HandoffDateRange,
} from '@/models/handoffDateRange';
import { useGlobalAlertStore } from '@/stores/useGlobalAlertStore';
import { spacing, unisonColors, useAppTheme } from '@/theme';

import { AiBatonListItem } from './AiBatonListItem';
import { UrgentHandoffCarousel } from './UrgentHandoffCarousel';

const PER_PAGE = 50;
const URGENT_PER_PAGE = 20;
const CONTACT_SEARCH_PER_PAGE = 20;
const CONTACT_ORDER_BY = 'last_action_occurred_at desc';

const keyExtractor = (item: HandoffItem) => item.id;

const ItemSeparator = () => <View style={styles.itemSeparator} />;

export const AiBatonList = () => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const router = useRouter();
  const showGlobalAlert = useGlobalAlertStore((state) => state.showGlobalAlert);

  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [selectedContactLabel, setSelectedContactLabel] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [contactSheetVisible, setContactSheetVisible] = useState(false);
  const [userSheetVisible, setUserSheetVisible] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [dateRange, setDateRange] = useState<HandoffDateRange>('all');
  const [dateSheetVisible, setDateSheetVisible] = useState(false);
  const [handoffPendingRelease, setHandoffPendingRelease] = useState<HandoffItem | null>(null);
  const [updatingHandoffId, setUpdatingHandoffId] = useState<string | null>(null);

  const debouncedContactSearch = useDebounce(contactSearch, 300);

  const { data: meData, loading: meLoading } = useQuery(MeDocument);
  const currentUserUid = meData?.me.backendAppUserUid;

  const { data: usersData, loading: usersLoading } = useQuery(GetUsersDocument, {
    variables: USER_PICKER_GET_USERS_VARIABLES,
    fetchPolicy: 'cache-and-network',
  });

  const { data: contactsData, loading: contactsLoading } = useQuery(GetContactsDocument, {
    variables: {
      input: {
        page: 1,
        perPage: CONTACT_SEARCH_PER_PAGE,
        search: debouncedContactSearch || undefined,
        orderBy: CONTACT_ORDER_BY,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !contactSheetVisible,
  });

  const selectedUserUid = useMemo(() => {
    if (selectedUserId == null) {
      return undefined;
    }

    return usersData?.users.data.find((user) => user.id === selectedUserId)?.uid;
  }, [selectedUserId, usersData?.users.data]);

  const createdAtFrom = useMemo(() => getHandoffDateRangeStart(dateRange, Date.now()), [dateRange]);

  const queryInput = useMemo(
    () => ({
      page: 1,
      perPage: PER_PAGE,
      contactId: selectedContactId ?? undefined,
      userUid: selectedUserUid,
      createdAtFrom,
      orderBy: 'SEVERITY_DESC' as const,
    }),
    [createdAtFrom, selectedContactId, selectedUserUid],
  );

  const urgentQueryInput = useMemo(
    () => ({
      page: 1,
      perPage: URGENT_PER_PAGE,
      contactId: selectedContactId ?? undefined,
      createdAtFrom,
    }),
    [createdAtFrom, selectedContactId],
  );

  // The carousel has its own query: the server decides what counts as urgent
  // (unassigned within the date range), so it is not derived from the loaded pages.
  const { data: urgentData } = useQuery(GetUrgentHandoffsDocument, {
    variables: { input: urgentQueryInput },
    fetchPolicy: 'cache-and-network',
    skip: meLoading,
  });

  const { data, loading, error, refetch, fetchMore, networkStatus } = useQuery(
    GetHandoffsDocument,
    {
      variables: { input: queryInput },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: meLoading,
    },
  );

  const { data: unreadStateData } = useQuery(GetHandoffUnreadStateDocument, {
    fetchPolicy: 'cache-and-network',
    skip: meLoading,
  });

  const unreadContactIds = useMemo(
    () => unreadStateData?.handoffUnreadState?.contactIds ?? [],
    [unreadStateData],
  );

  const unreadCreatedAts = useMemo(
    () => unreadStateData?.handoffUnreadState?.createdAts ?? [],
    [unreadStateData],
  );

  const unreadDateRanges = useMemo(
    () => getUnreadHandoffDateRanges(unreadCreatedAts, Date.now()),
    [unreadCreatedAts],
  );

  const hasUnreadByContact = unreadContactIds.length > 0;
  const hasUnreadByDate = unreadDateRanges.length > 0;

  const buildVariables = useMemo(
    () => (nextPage: number) => ({ input: { ...queryInput, page: nextPage } }),
    [queryInput],
  );

  const { isFetchingMore, handleEndReached } = usePaginatedFetchMore<GetHandoffsQuery>({
    networkStatus,
    data,
    entityKey: 'handoffs',
    fetchMore,
    buildVariables,
  });

  const { onRefresh, refreshing } = useRefreshHandler(refetch);

  // Claiming and releasing change which handoffs belong in each list, and that
  // membership is decided by the server, so both lists have to be reloaded.
  const listRefetchQueries = useMemo(
    () => [
      { query: GetHandoffsDocument, variables: { input: queryInput } },
      { query: GetUrgentHandoffsDocument, variables: { input: urgentQueryInput } },
    ],
    [queryInput, urgentQueryInput],
  );

  const [claimHandoff] = useMutation(ClaimHandoffDocument);
  const [releaseHandoff] = useMutation(ReleaseHandoffDocument);

  const handoffs = useMemo(() => data?.handoffs.data ?? [], [data]);

  const urgentHandoffs = useMemo(() => urgentData?.urgentHandoffs.data ?? [], [urgentData]);

  const sections = useMemo(() => getHandoffSections(handoffs), [handoffs]);

  // The urgent carousel lives in the list header, so an empty section list on its own
  // does not mean there is nothing to show.
  const isEmpty = handoffs.length === 0 && urgentHandoffs.length === 0;

  const userOptions = useMemo<readonly UserOption[]>(
    () => (usersData?.users.data ?? []).map((user) => ({ id: user.id, label: user.name })),
    [usersData?.users.data],
  );

  const contactOptions = useMemo(
    () =>
      (contactsData?.contacts.data ?? []).map((contact) => ({
        id: String(contact.id),
        label: contact.displayName,
      })),
    [contactsData?.contacts.data],
  );

  const runHandoffMutation = useCallback(
    async (handoffId: string, action: HandoffAction): Promise<boolean> => {
      setUpdatingHandoffId(handoffId);

      const mutate = action === 'claim' ? claimHandoff : releaseHandoff;

      try {
        await mutate({
          variables: { input: { handoffId } },
          refetchQueries: listRefetchQueries,
          awaitRefetchQueries: true,
        });

        return true;
      } catch {
        showGlobalAlert({ text: t(`aiTasks.error.${action}Failed`), variant: 'error' });

        return false;
      } finally {
        setUpdatingHandoffId(null);
      }
    },
    [claimHandoff, listRefetchQueries, releaseHandoff, showGlobalAlert, t],
  );

  const handleItemPress = useCallback(
    (handoffId: string) => {
      router.push(`/handoff/${handoffId}`);
    },
    [router],
  );

  // Claiming a baton takes the user straight into it, so they can start working
  // without hunting for it in the list afterwards.
  const handleClaimPress = useCallback(
    (handoffId: string) => {
      void runHandoffMutation(handoffId, 'claim').then((claimed) => {
        if (!claimed) {
          return;
        }

        handleItemPress(handoffId);
      });
    },
    [handleItemPress, runHandoffMutation],
  );

  const handleReleasePress = useCallback(
    (handoffId: string) => {
      const handoff = handoffs.find((item) => item.id === handoffId);

      if (!handoff) {
        return;
      }

      // Handing back someone else's baton affects another person's work, so confirm first.
      if (isHandoffAssignedToCurrentUser({ ...handoff, currentUserUid })) {
        void runHandoffMutation(handoffId, 'release');

        return;
      }

      setHandoffPendingRelease(handoff);
    },
    [currentUserUid, handoffs, runHandoffMutation],
  );

  const handleConfirmRelease = useCallback(() => {
    if (!handoffPendingRelease) {
      return;
    }

    const handoffId = handoffPendingRelease.id;

    setHandoffPendingRelease(null);
    void runHandoffMutation(handoffId, 'release');
  }, [handoffPendingRelease, runHandoffMutation]);

  const handleDismissRelease = useCallback(() => {
    setHandoffPendingRelease(null);
  }, []);

  const holderName = useMemo(() => {
    const holderUid = handoffPendingRelease?.userUid;

    if (holderUid == null) {
      return null;
    }

    return usersData?.users.data.find((user) => user.uid === holderUid)?.name ?? null;
  }, [handoffPendingRelease?.userUid, usersData?.users.data]);

  const handleSelectContact = useCallback(
    (contactId: string | null) => {
      setSelectedContactId(contactId);
      setSelectedContactLabel(
        contactId == null
          ? null
          : (contactOptions.find((contact) => contact.id === contactId)?.label ?? null),
      );
      setContactSheetVisible(false);
    },
    [contactOptions],
  );

  const handleSelectUser = useCallback((userId: number | null) => {
    setSelectedUserId(userId);
    setUserSheetVisible(false);
  }, []);

  const handleOpenContactSheet = useCallback(() => {
    setContactSheetVisible(true);
  }, []);

  const handleDismissContactSheet = useCallback(() => {
    setContactSheetVisible(false);
  }, []);

  const handleOpenUserSheet = useCallback(() => {
    setUserSheetVisible(true);
  }, []);

  const handleDismissUserSheet = useCallback(() => {
    setUserSheetVisible(false);
  }, []);

  const handleOpenDateSheet = useCallback(() => {
    setDateSheetVisible(true);
  }, []);

  const handleDismissDateSheet = useCallback(() => {
    setDateSheetVisible(false);
  }, []);

  const handleSelectDateRange = useCallback((range: HandoffDateRange) => {
    setDateRange(range);
    setDateSheetVisible(false);
  }, []);

  const allLabel = t('aiTasks.filter.all');

  const listHeader = useMemo(
    () => (
      <View style={styles.header}>
        <ScrollView
          contentContainerStyle={styles.filterRow}
          horizontal
          showsHorizontalScrollIndicator={false}
          testID="ai-baton-filter-row">
          <FilterDropdownChip
            label={t('aiTasks.filter.contact')}
            selected={selectedContactId !== null}
            showDot={hasUnreadByContact}
            testID="ai-baton-contact-filter-chip"
            value={selectedContactLabel ?? allLabel}
            onPress={handleOpenContactSheet}
          />
          <FilterDropdownChip
            label={t('aiTasks.filter.createdAt')}
            selected={dateRange !== 'all'}
            showDot={hasUnreadByDate}
            testID="ai-baton-date-filter-chip"
            value={t(HANDOFF_DATE_RANGE_TRANSLATION_KEYS[dateRange])}
            onPress={handleOpenDateSheet}
          />
          <FilterDropdownChip
            label={t('aiTasks.filter.personInCharge')}
            selected={selectedUserId !== null}
            testID="ai-baton-user-filter-chip"
            value={userOptions.find((user) => user.id === selectedUserId)?.label ?? allLabel}
            onPress={handleOpenUserSheet}
          />
        </ScrollView>
        <UrgentHandoffCarousel
          handoffs={urgentHandoffs}
          updatingHandoffId={updatingHandoffId}
          onClaim={handleClaimPress}
          onPress={handleItemPress}
        />
      </View>
    ),
    [
      allLabel,
      dateRange,
      handleClaimPress,
      handleItemPress,
      handleOpenContactSheet,
      handleOpenDateSheet,
      handleOpenUserSheet,
      hasUnreadByContact,
      hasUnreadByDate,
      selectedContactId,
      selectedContactLabel,
      selectedUserId,
      t,
      updatingHandoffId,
      urgentHandoffs,
      userOptions,
    ],
  );

  const renderItem = useCallback(
    ({ item }: { item: HandoffItem }) => (
      <AiBatonListItem
        currentUserUid={currentUserUid}
        isUpdating={updatingHandoffId === item.id}
        item={item}
        onClaim={handleClaimPress}
        onPress={handleItemPress}
        onRelease={handleReleasePress}
      />
    ),
    [currentUserUid, handleClaimPress, handleItemPress, handleReleasePress, updatingHandoffId],
  );

  const renderSectionHeader = useCallback(
    ({ section }: { section: SectionListData<HandoffItem, HandoffSection> }) => (
      <SectionDateHeader testID={`ai-baton-section-${section.key}`} title={t(section.titleKey)} />
    ),
    [t],
  );

  const refreshControl = useMemo(
    () => (
      <RefreshControl
        colors={[theme.colors.iconNdPrimary]}
        refreshing={refreshing}
        testID="ai-baton-list-refresh-control"
        tintColor={theme.colors.iconNdPrimary}
        onRefresh={onRefresh}
      />
    ),
    [onRefresh, refreshing, theme.colors.iconNdPrimary],
  );

  const renderList = () => {
    if ((loading || meLoading) && !data) {
      return <LoadingState />;
    }

    if (error && !data) {
      return <ErrorState message={t('aiTasks.loadError')} />;
    }

    return (
      <SectionList<HandoffItem, HandoffSection>
        contentContainerStyle={isEmpty ? styles.emptyListContent : styles.listContent}
        ItemSeparatorComponent={ItemSeparator}
        keyExtractor={keyExtractor}
        ListEmptyComponent={isEmpty ? AiBatonEmptyState : null}
        ListFooterComponent={<PaginationFooter isFetchingMore={isFetchingMore} />}
        ListHeaderComponent={listHeader}
        refreshControl={refreshControl}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        sections={sections}
        stickySectionHeadersEnabled={false}
        style={styles.list}
        testID="ai-baton-list"
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.5}
      />
    );
  };

  return (
    <>
      {renderList()}
      <ContactFilterSheet
        allOptionLabel={allLabel}
        contacts={contactOptions}
        loading={contactsLoading && contactOptions.length === 0}
        selectedContactId={selectedContactId}
        title={t('aiTasks.filter.selectContact')}
        unreadContactIds={unreadContactIds}
        visible={contactSheetVisible}
        onDismiss={handleDismissContactSheet}
        onSearchChange={setContactSearch}
        onSelect={handleSelectContact}
      />
      <ConfirmDialog
        body={
          holderName == null
            ? t('aiTasks.releaseOthersConfirm.bodyUnknownHolder')
            : t('aiTasks.releaseOthersConfirm.body', { name: holderName })
        }
        confirmLabel={t('aiTasks.releaseOthersConfirm.confirm')}
        dismissLabel={t('aiTasks.releaseOthersConfirm.dismiss')}
        title={t('aiTasks.releaseOthersConfirm.title')}
        testID="ai-baton-release-confirm-dialog"
        visible={handoffPendingRelease !== null}
        onConfirm={handleConfirmRelease}
        onDismiss={handleDismissRelease}
      />
      <DateRangeFilterSheet
        selectedRange={dateRange}
        title={t('aiTasks.filter.selectCreatedAt')}
        unreadRanges={unreadDateRanges}
        visible={dateSheetVisible}
        onDismiss={handleDismissDateSheet}
        onSelect={handleSelectDateRange}
      />
      <UserPickerSheet
        clearLabel={allLabel}
        currentUserId={selectedUserId}
        loading={usersLoading}
        showIcons={false}
        title={t('aiTasks.filter.selectPersonInCharge')}
        users={userOptions}
        visible={userSheetVisible}
        onDismiss={handleDismissUserSheet}
        onSelect={handleSelectUser}
      />
    </>
  );
};

const styles = StyleSheet.create({
  list: {
    backgroundColor: unisonColors.bgNlWeak,
  },
  listContent: {
    paddingHorizontal: spacing[4],
    paddingBottom: spacing[4],
  },
  emptyListContent: {
    flexGrow: 1,
    paddingHorizontal: spacing[4],
    paddingBottom: spacing[4],
  },
  header: {
    paddingTop: spacing[4],
  },
  filterRow: {
    columnGap: spacing[2],
    // Headroom so the unread dot's overhang beyond a filter chip's top-left
    // corner (2px up, 1px left) is not clipped by the scroll viewport edges.
    paddingLeft: 2,
    paddingTop: 2,
    paddingBottom: spacing[2],
  },
  itemSeparator: {
    height: spacing[2],
  },
});
